import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    IconButton,
    Chip,
    Avatar,
    Divider,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert
} from '@mui/material';
import { ArrowLeft, CheckCircle2, HelpCircle, SquarePen, ChevronDown, Image as ImageIcon, Clock, User } from 'lucide-react';
import { getCategoryById } from '../../../Services/CategoryManagement/CategoryManagementApi';

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        return JSON.parse(val);
    } catch {
        return [];
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return dateString || '--';
    }
};

const formatUserWithType = (name, type) => {
    if (!name && !type) return '--';
    if (name && type) return `${name} (${type})`;
    if (name) return name;
    return `(${type})`;
};

const CategoryDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [category, setCategory] = useState(location.state?.category || null);
    const [loading, setLoading] = useState(!location.state?.category && Boolean(id));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!category && id) {
            const fetchCategory = async () => {
                try {
                    setLoading(true);
                    const res = await getCategoryById(id);
                    if (res && res.success) {
                        setCategory(res.data);
                    } else if (res && res.data) {
                        setCategory(res.data);
                    } else {
                        setError("Failed to load category details.");
                    }
                } catch (err) {
                    console.error("Error fetching category:", err);
                    setError("Failed to fetch category details.");
                } finally {
                    setLoading(false);
                }
            };
            fetchCategory();
        }
    }, [category, id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={36} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error || !category) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh' }}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={() => navigate('/category-management')}
                        sx={{ backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0' }}
                    >
                        <ArrowLeft size={20} color="#222" />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#222' }}>
                        Category Details
                    </Typography>
                </Box>
                <Alert severity="error">
                    {error || "Category data not found. Please go back to the category listing."}
                </Alert>
            </Box>
        );
    }

    const suitabilityList = safeParseArray(category.suitabilityItems);
    const faqsList = safeParseArray(category.faqs);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh' }}>
            {/* Header / Action Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton disableFocusRipple disableRipple disableTouchRipple
                        onClick={() => navigate('/category-management')}
                        sx={{ backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', }}
                    >
                        <ArrowLeft size={20} color="#222" />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                            {category.name || 'Category Details'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25 }}>
                            Complete read-only specification for this category.
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    variant="contained"
                    startIcon={<SquarePen size={18} />}
                    onClick={() => navigate('/add-category', { state: { category } })}
                    sx={{
                        backgroundColor: '#FFBD65',
                        color: '#0F172A',
                        textTransform: 'none',
                        px: 2.5,
                        py: 1,
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                >
                    Edit Category
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column: Visual Assets Card & Metadata */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #E2E8F0', mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
                            Visual Assets
                        </Typography>

                        {/* Category Image */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 1 }}>
                                CATEGORY IMAGE
                            </Typography>
                            {category.image ? (
                                <Box
                                    component="img"
                                    src={category.image}
                                    alt={category.name}
                                    sx={{
                                        width: '100%',
                                        height: 180,
                                        objectFit: 'cover',
                                        borderRadius: '10px',
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                    }}
                                />
                            ) : (
                                <Box sx={{ height: 140, borderRadius: '10px', border: '1px dashed #CBD5E1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                                    <ImageIcon size={32} />
                                    <Typography variant="caption" sx={{ mt: 1 }}>No Image Provided</Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Category Icon */}
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 1 }}>
                                CATEGORY ICON
                            </Typography>
                            {category.icon ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        src={category.icon}
                                        alt={category.name}
                                        variant="rounded"
                                        sx={{ width: 64, height: 64, borderRadius: '10px', border: '1px solid #E2E8F0' }}
                                    />
                                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                                        Displayed across list views & cards
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ height: 70, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px dashed #CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                                    <Typography variant="caption">No Icon Provided</Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    {/* Quick System Info Card */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
                            System Information
                        </Typography>

                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                                    Display Order
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                                    {category.displayOrder !== undefined && category.displayOrder !== null ? category.displayOrder : '--'}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                                    Status
                                </Typography>
                                <Chip
                                    label={category.status ? "Active" : "Inactive"}
                                    size="small"
                                    sx={{
                                        bgcolor: category.status ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                        color: category.status ? '#4caf50' : '#f44336',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        borderRadius: '6px'
                                    }}
                                />
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <User size={14} /> Created By
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                                    {formatUserWithType(category.cuName, category.cuType)}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Clock size={14} /> Created At
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', mt: 0.5 }}>
                                    {formatDate(category.createdAt)}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <User size={14} /> Updated By
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                                    {formatUserWithType(category.uuName, category.uuType)}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Clock size={14} /> Updated At
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', mt: 0.5 }}>
                                    {formatDate(category.updatedAt)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Right Column: General Info, Suitability & FAQs */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2.5 }}>
                            General Information
                        </Typography>

                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    CATEGORY NAME
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                                    {category.name || '--'}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    SLUG
                                </Typography>
                                <Chip
                                    label={category.slug || '--'}
                                    size="small"
                                    sx={{ bgcolor: '#F1F5F9', color: '#334155', fontWeight: 500, fontFamily: 'monospace' }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    DESCRIPTION
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#334155', bgcolor: '#F8FAFC', p: 2, borderRadius: '8px', border: '1px solid #F1F5F9', whiteSpace: 'pre-line' }}>
                                    {category.description || '--'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Suitability Items */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle2 size={20} color="#16A34A" /> Suitability Items
                            </Typography>
                            <Chip label={`${suitabilityList.length} Items`} size="small" color="primary" variant="outlined" />
                        </Box>

                        {suitabilityList.length > 0 ? (
                            <Stack spacing={1.5}>
                                {suitabilityList.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            p: 1.75,
                                            borderRadius: '8px',
                                            bgcolor: '#F8FAFC',
                                            border: '1px solid #E2E8F0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}
                                    >
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16A34A', flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                            {typeof item === 'object' && item !== null ? item.name || item.title || item.label || '' : item}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1' }}>
                                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                    No suitability items specified.
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* FAQs */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HelpCircle size={20} color="#0284C7" /> Frequently Asked Questions (FAQs)
                            </Typography>
                            <Chip label={`${faqsList.length} FAQs`} size="small" color="secondary" variant="outlined" />
                        </Box>

                        {faqsList.length > 0 ? (
                            <Stack spacing={1.5}>
                                {faqsList.map((faq, idx) => (
                                    <Accordion key={idx} variant="outlined" sx={{ borderRadius: '8px !important', border: '1px solid #E2E8F0' }}>
                                        <AccordionSummary expandIcon={<ChevronDown size={18} />}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                                                Q: {typeof faq?.question === 'object' && faq?.question !== null ? faq.question.name || '' : (faq?.question || '--')}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ borderTop: '1px solid #F1F5F9', bgcolor: '#F8FAFC' }}>
                                            <Typography variant="body2" sx={{ color: '#475569', whiteSpace: 'pre-line' }}>
                                                A: {typeof faq?.answer === 'object' && faq?.answer !== null ? faq.answer.name || '' : (faq?.answer || '--')}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1' }}>
                                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                    No FAQs specified for this category.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CategoryDetails;
